import { isValidElement } from 'react';

import { useMergeRefs } from '@xemplo/ref-utils';

import { usePopoverContext } from '../../hooks/use-popover-context';
import {
  applyAsChild,
  getChildrenRef,
  isRenderProp,
  mergeEventHandlers,
} from '../../utils/popover.helpers';
import { ElementWithRef, RenderProp } from '../../utils/popover-helpers.types';

type PopoverTriggerProps<T extends HTMLElement = HTMLElement> = {
  children: ElementWithRef<T> | RenderProp<T>;
  usePropRefKey?: string;
};

function PopoverTriggerInternal<T extends HTMLElement = HTMLElement>({
  children,
  usePropRefKey,
}: PopoverTriggerProps<T>) {
  const { open, refs, setOpen, getReferenceProps } = usePopoverContext();

  // Support function-as-child
  const childNode = isRenderProp(children) ? children({ open, setOpen }) : children;
  const mergedRef = useMergeRefs([refs.setReference, getChildrenRef<T>(childNode)]);

  const floatingProps = getReferenceProps();

  // Some of the trigger props may be event handlers, so we merge them
  // with the child's props to ensure all event handlers are preserved.
  // This is especially important for event handlers like onClick, onFocus, etc.
  const mergedHandlers = mergeEventHandlers(floatingProps, childNode.props);

  const props = {
    asChild: true,
    children: childNode,
    ref: usePropRefKey ? null : mergedRef,
    propsToInject: {
      ...floatingProps,
      ...mergedHandlers,
      ...(usePropRefKey ? { [usePropRefKey]: refs.setReference } : {}),
    },
  };

  return applyAsChild<T>(props);
}

export function PopoverTrigger<T extends HTMLElement = HTMLElement>({
  children,
  usePropRefKey,
}: PopoverTriggerProps<T>) {
  if (children == null || (!isRenderProp<T>(children) && !isValidElement(children))) {
    throw new Error('PopoverTrigger requires a valid React element as children.');
  }

  return <PopoverTriggerInternal<T> children={children} usePropRefKey={usePropRefKey} />;
}
